import { ApiResponse, ApiService } from './api.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import { Series } from '@models/series';
import { SyncStatusService } from './sync-status.service';

@Injectable({
  providedIn: 'root',
})
export class SeriesDataService extends SyncStatusService implements OnDestroy {
  public series: Observable<Series[]>;
  private _series: BehaviorSubject<Series[]>;
  private _loaded = false;
  private data: {
    series: Series[];
  };
  private _destroy = new Subject<void>();
  private URL = 'series';

  constructor(private api: ApiService) {
    super();
    this.data = {
      series: [],
    };
    this._series = new BehaviorSubject([]);
    this.series = this._series.asObservable();
    this._publish();
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._clearSyncTimeout();
  }

  seriesExist(): boolean {
    return this.data.series.length > 0;
  }

  byId(id: string): Series {
    return this.data.series.find((p) => p.id === id);
  }

  /**
   * Cached loader for Series
   *
   * @returns {Promise<Array<Series>>}
   * @memberof SeriesDataService
   */
  async load(force = false): Promise<Array<Series>> {
    if (this._loaded && !force) {
      return Promise.resolve([...this.data.series]);
    }
    const dbLoadStart: number = new Date().getTime();
    try {
      const docs: ApiResponse = await this.api.allDocs(this.URL);
      this._lastSync = docs.until;
      this.data.series = docs.results.map((payload) =>
        this._payloadToInstance(payload),
      );
      this._loaded = true;
      this._publish();

      const delta = new Date().getTime() - dbLoadStart;
      const numSeries = (this.data.series && this.data.series.length) || 0;
      console.debug('Database', delta, 'Load', 'Series');
      console.debug('Num Series', numSeries);
    } catch (e) {
      console.error(e);
      throw e;
    }
    return this.data.series;
  }

  public loadById(id: string): Promise<Series> {
    return this.api
      .get<Series>(this.URL, id)
      .then((p) => this._payloadToInstance(p));
  }

  public getById(id: string): Series {
    return this.data.series.find((obj) => obj.id === id);
  }

  public unload(): void {
    'Logged out then clear series';
    this.data.series = [];
    this._publish();
  }

  protected async _getLatestFromApi(): Promise<Array<Series>> {
    const docs: ApiResponse = await this.api.allDocs(this.URL, this._lastSync);
    this._lastSync = docs.until;
    return docs.results;
  }

  protected _addUpdate(payload: Series) {
    const series: Series = this._payloadToInstance(payload);
    this._processChange(series);
    return series;
  }

  protected _publish(): void {
    // new object list to trigger change detection and isolate changes.
    if (!this._series.closed) {
      this._series.next([...this.data.series]);
    }
  }

  private _payloadToInstance(series: Series): Series {
    const instance = new Series({ ...series });
    return instance;
  }

  private _processChange(series: Series): Series {
    this.data.series = [
      ...this.data.series.filter((p) => p.id !== series.id),
      series,
    ];
    return series;
  }
}
